import { FunctionComponent, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import SMSMessageEditor from './SMSMessageEditor';
import SMSPhonePreview from './SMSPhonePreview';
import styles from './SMSContentEditor.module.scss';
import { type SMSContent } from '@spike/automation-model';
import MergeTagPicker from '../MergeTags/MergeTagPicker';
import { useMergeTags } from '../../hooks/useMergeTags';

export type { SMSContent };

interface Props {
    initialContent: SMSContent;
    trigger?: { event?: string; anchor?: string };
    onSave: (content: SMSContent) => void;
    onClose: () => void;
}

const UNSUBSCRIBE_DEFAULT = 'Reply STOP to unsubscribe';

const SMSContentEditor: FunctionComponent<Props> = ({ initialContent, trigger = {}, onSave, onClose }) => {
    const [message, setMessage] = useState(initialContent.message);
    const messageRef = useRef<HTMLTextAreaElement>(null);
    const { tags: mergeTags } = useMergeTags(trigger);
    const [unsubscribeMessage] = useState(initialContent.unsubscribeMessage || UNSUBSCRIBE_DEFAULT);

    const handleSave = () => {
        onSave({ message, unsubscribeMessage });
    };

    return (
        <div
            className={styles.overlay}
            onClick={e => e.stopPropagation()}
        >
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLogo}>
                    <img
                        className={styles.logo}
                        src="/images/logo/logo.png"
                        alt="Cuddles"
                    />
                </div>
                <div className={styles.headerActions}>
                    <Button
                        label="Save"
                        variant="green"
                        onClick={handleSave}
                        disabled={!message.trim()}
                    />
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className={styles.body}>
                {/* Left: editor */}
                <div className={styles.editorPane}>
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Text Message</h3>
                        <div className={styles.fieldLabel}>Message</div>
                        <SMSMessageEditor
                            value={message}
                            onChange={setMessage}
                            textareaRef={messageRef}
                            hasMergeTags={mergeTags.length > 0}
                            toolbar={
                                <MergeTagPicker
                                    trigger={trigger}
                                    inputRef={messageRef}
                                    value={message}
                                    onChange={setMessage}
                                />
                            }
                        />
                    </div>

                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Unsubscribe Message</h3>
                        <div className={styles.unsubscribeBox}>
                            <span className={styles.unsubscribeText}>{unsubscribeMessage}</span>
                            <span className={styles.unsubscribeNote}>*</span>
                        </div>
                    </div>
                </div>

                {/* Right: preview */}
                <div className={styles.previewPane}>
                    <h3 className={styles.previewTitle}>Preview Message</h3>
                    <div className={styles.previewPhoneWrapper}>
                        <SMSPhonePreview
                            message={message}
                            unsubscribeMessage={unsubscribeMessage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSContentEditor;
